// external libraries
import { useTranslation } from 'react-i18next';

// constants
import { ROSTER_PLAN_MODE_META } from '../RosterPlan.consts';

// hooks
import useRosterPlanMode from './useRosterPlanMode';

// stores
import { useRosterPlanStore } from '../RosterPlan.stores';

// types
import type { ToolbarAction } from 'components/shared/ToolbarSet/ToolbarSet.types';

/**
 * The toolbar action behind which the mode switch sits: a menu with one entry per mode the grants
 * allow, the active one selected. The action itself carries the active mode's icon and names it in
 * its tooltip, which is what the Dojo selector showed as its value.
 *
 * A menu on a toolbar action rather than a select: it is the pattern the toolbar has for a choice
 * between a few values (FlexMatch's view mode), and it keeps working when the toolbar is docked
 * vertically or in zen mode, where a select has no room.
 *
 * Shown with a single entry too, as the Dojo selector was: a planner with view access alone still
 * sees which mode the screen is in.
 *
 * Right-aligned, and last among the screen's actions so it sits just before the toolbar's own menu:
 * the mode is a screen-wide setting, not a period control, and the Dojo top bar placed its selector
 * at the far right of its own row, apart from the period buttons.
 *
 * No divider: a menu action does not render `leftDivider`/`rightDivider` (`ToolbarActionMenu`
 * draws only its button), so setting one would be a no-op.
 *
 * No `ariaLabel`: the toolbar names an icon-only button by its `tooltipLabel`, which carries the
 * active mode ("Modus: Plannen"). An explicit label would replace that with the bare word.
 */
const useRosterPlanModeAction = (): ToolbarAction => {
  const { t } = useTranslation('common', { keyPrefix: 'rosterPlan.mode' });
  const { mode, availableModes } = useRosterPlanMode();
  const setMode = useRosterPlanStore((state) => state.setMode);

  const activeMeta = ROSTER_PLAN_MODE_META[mode];

  return {
    tooltipLabel: t('tooltip', { mode: t(activeMeta.labelKey) }),
    id: 'roster-plan-mode',
    icon: activeMeta.icon,
    contextMenu: availableModes.map((availableMode) => ({
      label: t(ROSTER_PLAN_MODE_META[availableMode].labelKey),
      icon: ROSTER_PLAN_MODE_META[availableMode].icon,
      onClick: () => setMode(availableMode),
      selected: availableMode === mode
    })),
    actionDataTourId: 'roster-plan-mode',
    alignment: 'right'
  };
};

export default useRosterPlanModeAction;
